import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LuBed,
  LuChevronLeft,
  LuClock3,
  LuCoffee,
  LuDroplets,
  LuExpand,
  LuFileText,
  LuLeaf,
  LuMail,
  LuMapPin,
  LuPhone,
  LuSparkles,
  LuTicket,
  LuWind,
  LuWifi
} from "react-icons/lu";
import { MdCheckCircle } from "react-icons/md";
import ProgressBar from "./ProgressBar";
import {
  getPricingPeriod,
  getRoomRatesForDate,
  ROOMS_CATALOG
} from "./roomCatalog";
const SERVICE_ICON_MAP = {
  Bed: LuBed,
  Maximize: LuExpand,
  Droplets: LuDroplets,
  Wind: LuWind,
  Wifi: LuWifi,
  Coffee: LuCoffee,
  Sparkles: LuSparkles
};
const parseInputDate = (value) => {
  const parts = value.split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return null;
  }
  return parsed;
};
const formatDateForDisplay = (value) => {
  const parsed = parseInputDate(value);
  return parsed ? parsed.toLocaleDateString() : value;
};
const getServiceIcon = (iconName) => {
  const IconComponent = SERVICE_ICON_MAP[iconName] ?? LuSparkles;
  return <IconComponent className="h-5 w-5" />;
};
const Booking = () => {
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingQuery = useMemo(() => {
    const resortId = searchParams.get("resortId");
    const roomType = searchParams.get("roomType");
    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");
    const guests2 = Number(searchParams.get("guests") ?? "1");
    const selectedImage = searchParams.get("image");
    return {
      resortId,
      roomType,
      checkIn,
      checkOut,
      guests: Number.isFinite(guests2) && guests2 > 0 ? guests2 : 1,
      selectedImage
    };
  }, [searchParams]);
  const room = useMemo(() => {
    const { resortId, roomType, checkIn, checkOut, guests: guests2, selectedImage } = bookingQuery;
    if ((roomType !== "double" && roomType !== "junior-suite" && roomType !== "master-suite") || !checkIn || !checkOut) {
      return null;
    }
    const checkInDate = parseInputDate(checkIn);
    const checkOutDate = parseInputDate(checkOut);
    if (!checkInDate || !checkOutDate) {
      return null;
    }
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1e3 * 60 * 60 * 24));
    if (nights < 7) {
      return null;
    }
    const baseRoom = ROOMS_CATALOG.find((item) => item.type === roomType && (!resortId || item.resortId === resortId));
    if (!baseRoom) {
      return null;
    }
    const pricingPeriod = getPricingPeriod(checkInDate);
    if (!pricingPeriod) {
      return null;
    }
    const season = pricingPeriod.season;
    const rates = getRoomRatesForDate(baseRoom, checkInDate);
    const price = guests2 === 1 ? rates.single : rates.double;
    const bookingImage = selectedImage && selectedImage.startsWith("/images/") ? selectedImage : baseRoom.image;
    return {
      ...baseRoom,
      image: bookingImage,
      season,
      price,
      totalPrice: price * nights,
      nights,
      periodLabel: pricingPeriod.label,
      rates,
      checkIn,
      checkOut
    };
  }, [bookingQuery]);
  const guests = bookingQuery.guests;
  const [bookingDetails, setBookingDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    zip: "",
    requests: "",
    arrival: "Morning",
    coupon: "",
    terms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!room) {
      router("/search", { replace: true });
    }
  }, [room, router]);
  if (!room) {
    return null;
  }
  const getTranslatedServiceText = (serviceText) => {
    switch (serviceText) {
      case "Sea-facing balcony":
        return "Meerblick-Balkon";
      case "Large double bed":
        return "Gro\xDFes Doppelbett";
      case "Bathroom with toilet and shower":
        return "Bad mit Toilette und Dusche";
      case "Hairdryer":
        return "Haartrockner";
      case "Air conditioning (recommended to avoid during Ayurveda treatment)":
        return "Klimaanlage (w\xE4hrend Ayurveda-Behandlungen nur sparsam nutzen)";
      case "Large ceiling fan":
        return "Gro\xDFer Deckenventilator";
      case "Free WiFi":
        return "Kostenloses WLAN";
      case "Mosquito net":
        return "Moskitonetz";
      case "Daily fresh hot and cold drinking water":
        return "T\xE4glich frisches warmes und kaltes Trinkwasser";
      case "One free small room safe":
        return "Kostenloser kleiner Safe im Zimmer";
      default:
        return serviceText;
    }
  };
  const handleChange = (event) => {
    const { name, value, type } = event.target;
    const checked = event.target instanceof HTMLInputElement && event.target.type === "checkbox" ? event.target.checked : false;
    setBookingDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };
  const handleBooking = (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    if (!bookingDetails.terms) {
      setError("Bitte akzeptieren Sie die Allgemeinen Gesch\xE4ftsbedingungen und die Datenschutzerkl\xE4rung.");
      setLoading(false);
      return;
    }
    const bookingData = {
      ...bookingDetails,
      roomName: room.name,
      resortId: room.resortId,
      roomType: room.type,
      checkIn: room.checkIn,
      checkOut: room.checkOut,
      totalPrice: room.totalPrice,
      guests
    };
    localStorage.setItem("bookingData", JSON.stringify(bookingData));
    setTimeout(() => {
      setLoading(false);
      const params = new URLSearchParams({
        resortId: room.resortId,
        roomType: room.type,
        checkIn: room.checkIn,
        checkOut: room.checkOut,
        guests: String(guests)
      });
      router(`/checkout?${params.toString()}`);
    }, 500);
  };
  return <div className="min-h-screen bg-white">
    <section className="relative h-[64vh] min-h-[240px] overflow-hidden md:h-[40vh] lg:h-[76vh]">
      <img
        src={room.image}
        alt={`${room.name} Buchungsdetails`}
        className="object-cover object-center" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} loading="eager" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-transparent" />

      <div className="relative mx-auto flex h-full w-full max-w-7xl items-end px-10 pb-8 pt-32 sm:px-6 sm:pb-8 sm:pt-32 md:pb-10 md:pt-10 lg:px-8">
        <div className="max-w-3xl text-white">
          <h1 className="mt-10 text-3xl font-light leading-tight sm:text-4xl md:text-5xl">
            Buchungsdetails prüfen
          </h1>
          <p className="mt-3 max-w-xl text-sm text-white/90 sm:max-w-2xl md:text-base">
            Bitte prüfen Sie die Buchungsdetails für {room.name} und tragen Sie anschließend Ihre Gästedaten ein.
          </p>
        </div>
      </div>
    </section>

    <div className="pt-3 sm:pt-4 md:pt-6">
      <ProgressBar currentStep="booking" />
    </div>

    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 md:py-12">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="lg:col-span-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border-l-4 border-[#1f4d3c] bg-white p-4 shadow-lg sm:p-5 lg:sticky lg:top-4 lg:p-6"
          >
            <h3 className="mb-4 flex items-center text-lg font-light text-gray-900 sm:text-xl md:text-2xl">
              <MdCheckCircle className="mr-2 h-6 w-6 text-[#1f4d3c]" />
              Buchungsdetails
            </h3>

            <div className="relative mb-4 h-40 w-full overflow-hidden rounded-xl sm:h-48 md:h-56">
              <img src={room.image} alt={room.name} className="object-cover" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} loading="lazy" />
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="mb-2 text-lg font-light text-[#1f4d3c] md:text-xl">{room.name}</h4>
                <span className="inline-block rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                  Buchungsübersicht
                </span>
              </div>

              <div className="space-y-2 border-t border-gray-200 pt-3 text-sm md:text-base">
                <div className="flex justify-between">
                  <span className="text-gray-600">Check-in</span>
                  <span className="font-medium text-gray-900">{formatDateForDisplay(room.checkIn)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Check-out</span>
                  <span className="font-medium text-gray-900">{formatDateForDisplay(room.checkOut)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Anzahl der Nächte</span>
                  <span className="font-medium text-gray-900">{room.nights} Nächte</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Anzahl der Gäste</span>
                  <span className="font-medium text-gray-900">{guests} Personen</span>
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <div>
                    <span className="block text-gray-600">Pro Nacht</span>
                    <span className="text-xs text-gray-500">
                      {guests === 1 ? "1 Person" : "2 Personen"} • {room.periodLabel}
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">EUR {room.price}</span>
                </div>
                <div className="-mx-3 flex justify-between rounded-lg bg-gradient-to-r from-emerald-50 to-green-50 px-3 p-3 text-lg font-bold">
                  <span className="text-emerald-800">Gesamtbetrag</span>
                  <span className="text-[#1f4d3c]">EUR {room.totalPrice.toFixed(2)}</span>
                </div>
                <p className="pt-2 text-xs italic text-emerald-700">Aufenthaltssteuer und Mehrwertsteuer werden separat berechnet.</p>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <p className="mb-3 text-sm font-medium text-gray-700">Preise: gültig von {room.periodLabel}</p>

                <div
                  className={`mb-2 rounded-lg border p-2.5 ${room.season === "Summer" ? "border-amber-300 bg-amber-100/50" : "border-gray-200 bg-white"}`}
                >
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-700">Gültiger Preis</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">1 Person</span>
                      <span className="font-semibold text-gray-700">EUR {room.rates.single}/Nacht</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">2 Personen</span>
                      <span className="font-semibold text-gray-700">EUR {room.rates.double}/Nacht</span>
                    </div>
                  </div>
                </div>
              </div>

              {room.services.length > 0 && <div className="border-t border-gray-200 pt-3">
                <p className="mb-3 flex items-center text-sm font-medium text-gray-700">
                  <LuLeaf className="mr-2 h-4 w-4 text-[#1f4d3c]" />
                  Zimmerausstattung und Services
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {room.services.map((service) => <div
                    key={service.text}
                    className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2 text-xs text-gray-600 md:text-sm"
                  >
                    <span className="flex-shrink-0 text-[#1f4d3c]">{getServiceIcon(service.icon)}</span>
                    <span>{getTranslatedServiceText(service.text)}</span>
                  </div>)}
                </div>
              </div>}
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="rounded-2xl border-l-4 border-[#1f4d3c] bg-white p-6 shadow-lg md:p-8"
          >
            <h2 className="mb-6 text-2xl font-light text-gray-900 md:text-3xl">Gästerdaten</h2>

            {error && <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700"
            >
              <span className="text-xl">!</span>
              <p>{error}</p>
            </motion.div>}

            <form onSubmit={handleBooking} className="space-y-6">
              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">Name</label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    name="firstName"
                    value={bookingDetails.firstName}
                    onChange={handleChange}
                    placeholder="Vorname"
                    required
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={bookingDetails.lastName}
                    onChange={handleChange}
                    placeholder="Nachname"
                    required
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                  />
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">Kontaktinformationen</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <LuMail className="h-5 w-5 flex-shrink-0 text-[#1f4d3c]" />
                    <input
                      type="email"
                      name="email"
                      value={bookingDetails.email}
                      onChange={handleChange}
                      placeholder="E-Mail-Adresse"
                      required
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <LuPhone className="h-5 w-5 flex-shrink-0 text-[#1f4d3c]" />
                    <input
                      type="tel"
                      name="phone"
                      value={bookingDetails.phone}
                      onChange={handleChange}
                      placeholder="Telefonnummer"
                      required
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">Adresse</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <LuMapPin className="mt-2 h-5 w-5 flex-shrink-0 text-[#1f4d3c]" />
                    <input
                      type="text"
                      name="address"
                      value={bookingDetails.address}
                      onChange={handleChange}
                      placeholder="Straße und Hausnummer"
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <input
                      type="text"
                      name="city"
                      value={bookingDetails.city}
                      onChange={handleChange}
                      placeholder="Ort / Stadt"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                    <input
                      type="text"
                      name="country"
                      value={bookingDetails.country}
                      onChange={handleChange}
                      placeholder="Land"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                    <input
                      type="text"
                      name="zip"
                      value={bookingDetails.zip}
                      onChange={handleChange}
                      placeholder="Postleitzahl"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">
                  <span className="flex items-center gap-2">
                    <LuClock3 className="h-5 w-5 text-[#1f4d3c]" />
                    Ankunftszeit
                  </span>
                </label>
                <select
                  name="arrival"
                  value={bookingDetails.arrival}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                >
                  <option value="Morning">Vormittag</option>
                  <option value="Afternoon">Nachmittag</option>
                  <option value="Evening">Abend</option>
                </select>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">
                  <span className="flex items-center gap-2">
                    <LuFileText className="h-5 w-5 text-[#1f4d3c]" />
                    Zusätzliche Wünsche
                  </span>
                </label>
                <textarea
                  name="requests"
                  value={bookingDetails.requests}
                  onChange={handleChange}
                  placeholder="Bitte geben Sie Wünsche, Allergien oder besondere Anlässe an."
                  className="h-24 w-full resize-none rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:h-32 md:text-base"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-gray-900 md:text-base">
                  <span className="flex items-center gap-2">
                    <LuTicket className="h-5 w-5 text-[#1f4d3c]" />
                    Gutscheincode (optional)
                  </span>
                </label>
                <input
                  type="text"
                  name="coupon"
                  value={bookingDetails.coupon}
                  onChange={handleChange}
                  placeholder="z. B. WELCOME10"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black placeholder:text-gray-500 [color-scheme:light] transition-colors hover:border-[#1f4d3c] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-300 md:py-3 md:text-base"
                />
              </div>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 md:p-5">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    name="terms"
                    checked={bookingDetails.terms}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 cursor-pointer rounded text-[#1f4d3c] focus:ring-2 focus:ring-emerald-300"
                  />
                  <span className="text-sm text-gray-700 md:text-base">
                    Ich habe die Allgemeinen Geschäftsbedingungen gelesen und akzeptiert,
                    <Link
                      to="/contact-us"
                      className="font-semibold text-[#1f4d3c] transition-colors hover:text-[#1a3d2e] hover:underline"
                    >
                      Allgemeine Geschäftsbedingungen
                    </Link>
                    und
                    <Link
                      to="/contact-us"
                      className="font-semibold text-[#1f4d3c] transition-colors hover:text-[#1a3d2e] hover:underline"
                    >
                      Datenschutzrichtlinie
                    </Link>
                    überprüft.
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => router(-1)}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-300 px-4 py-3 text-sm font-semibold text-gray-800 shadow-md transition-all hover:bg-gray-400 hover:shadow-lg md:py-4 md:text-base"
                >
                  <LuChevronLeft className="h-5 w-5" />
                  Zurück
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1f4d3c] px-4 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#1a3d2e] hover:shadow-lg disabled:bg-emerald-300 md:py-4 md:text-base"
                >
                  {loading ? <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Wird verarbeitet...
                  </> : <>
                    <MdCheckCircle className="h-5 w-5" />
                    Weiter zur Zahlung
                  </>}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  </div>;
};
export default Booking;
